/**
 * FeedbackDialog - lets users send feedback to the developer.
 *
 * This is the only feature that sends anything off the device. It sends
 * ONLY what the user types (plus device type), never inventory data.
 * Posts to the /api/feedback serverless function, which files a GitHub issue.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { MessageSquare, Bug, Lightbulb, HelpCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const TYPES = [
  { value: 'bug', label: 'Bug', icon: Bug },
  { value: 'idea', label: 'Idea', icon: Lightbulb },
  { value: 'other', label: 'Other', icon: HelpCircle },
];

const MAX_MESSAGE = 4000;

function getDeviceInfo() {
  const ua = navigator.userAgent;
  let platform = 'Other';
  if (/iPhone|iPod/.test(ua)) platform = 'iPhone';
  else if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) platform = 'iPad';
  else if (/Android/.test(ua)) platform = 'Android';
  else if (/Windows/.test(ua)) platform = 'Windows';
  else if (/Macintosh/.test(ua)) platform = 'Mac';
  const installed = window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;
  return { platform, installed };
}

export function FeedbackDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('idea');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot - hidden from real users
  const [sending, setSending] = useState(false);
  const device = getDeviceInfo();

  const reset = () => {
    setType('idea');
    setMessage('');
    setEmail('');
    setWebsite('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 5) {
      toast.error('Please write a bit more.');
      return;
    }

    setSending(true);
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, message, email, website, device }),
      });
      const result = await response.json().catch(() => ({}));
      // Require the endpoint's explicit ok - a static fallback page can return 200 too
      if (!response.ok || result.ok !== true) {
        throw new Error(result.error || 'Could not send feedback right now.');
      }
      toast.success('Thanks! Your feedback was sent.');
      reset();
      setOpen(false);
    } catch (error) {
      console.error('[Feedback] Send failed:', error);
      toast.error(error.message === 'Failed to fetch'
        ? 'You appear to be offline. Please try again later.'
        : error.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="rounded-sm" data-testid="open-feedback-btn">
            <MessageSquare className="w-4 h-4 mr-2" />
            Send Feedback
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Send Feedback
          </DialogTitle>
          <DialogDescription>
            Report a bug or suggest an idea. Only what you type below is sent. Your inventory never leaves this device.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="feedback-form">
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Feedback type">
            {TYPES.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={type === value}
                onClick={() => setType(value)}
                className={`flex flex-col items-center gap-1 p-3 rounded-sm border text-sm transition-colors ${
                  type === value
                    ? 'border-secondary bg-secondary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:border-muted-foreground'
                }`}
                data-testid={`feedback-type-${value}`}
              >
                <Icon className="w-5 h-5" />
                {label}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-message">Message</Label>
            <Textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
              placeholder={type === 'bug' ? 'What happened, and what did you expect?' : 'What would make Arms Room better?'}
              rows={5}
              className="rounded-sm"
              data-testid="feedback-message"
            />
            <p className="text-xs text-muted-foreground text-right">{message.length}/{MAX_MESSAGE}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-email">Email (optional, if you'd like a reply)</Label>
            <Input
              id="feedback-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="rounded-sm"
              data-testid="feedback-email"
            />
          </div>

          {/* Honeypot field - hidden from people, bots tend to fill it */}
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
          />

          <p className="text-xs text-muted-foreground">
            Also included: device type ({device.platform}, {device.installed ? 'installed app' : 'browser tab'}).
          </p>

          <DialogFooter>
            <Button type="submit" disabled={sending || message.trim().length < 5} className="rounded-sm" data-testid="feedback-submit">
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackDialog;
